import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { MessageSquare } from 'react-feather';
import Table from '../ui/Table';
import {
  SIN_VALOR,
  formatearNumero,
  formatearPuntos,
  nombreAlumno,
  nombreEvaluador,
  tieneComentario,
} from '../../features/notasD2l';
import colors from '../../theme/colors';

const secundarioStyle = {
  color: colors.textSecondary,
  fontSize: '0.75rem',
  marginTop: '2px',
};

const numeroStyle = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

const texto = (valor) => (typeof valor === 'string' && valor.trim() ? valor : SIN_VALOR);

const CeldaDoble = ({ principal = '', secundario = '', destacado = false }) => (
  <div>
    <div style={{ fontWeight: destacado ? 600 : 400 }}>{principal || SIN_VALOR}</div>
    {secundario && <div style={secundarioStyle}>{secundario}</div>}
  </div>
);

CeldaDoble.propTypes = {
  principal: PropTypes.string,
  secundario: PropTypes.string,
  destacado: PropTypes.bool,
};

// Table invoca Cell con { original, value, row }. Se desempaqueta aquí para que
// react/prop-types no trate cada renderer de celda como un componente sin propTypes.
const conFila = (render) => (celda) => render(celda.original);

const construirColumnas = (onVerComentario) => [
  {
    Header: 'Nombre',
    id: 'nombre',
    width: 140,
    Cell: conFila((original) => (
      <CeldaDoble principal={original.nombre} secundario={original.usuario} destacado />
    )),
  },
  {
    Header: 'Apellidos',
    id: 'apellidos',
    width: 160,
    Cell: conFila((original) => <CeldaDoble principal={original.apellido} destacado />),
  },
  {
    Header: 'Oferta',
    id: 'oferta',
    width: 200,
    Cell: conFila((original) => (
      <CeldaDoble principal={original.nombre_oferta} secundario={original.codigo_oferta} />
    )),
  },
  {
    Header: 'Elemento',
    id: 'elemento',
    width: 180,
    Cell: conFila((original) => (
      <CeldaDoble principal={original.nombre_elemento} secundario={original.categoria_elemento} />
    )),
  },
  {
    Header: 'Puntos',
    id: 'puntos',
    width: 90,
    Cell: conFila((original) => (
      <span style={numeroStyle}>
        {formatearPuntos(original.numerador_puntos, original.denominador_puntos)}
      </span>
    )),
  },
  {
    Header: 'Ponderación',
    id: 'ponderacion',
    width: 100,
    Cell: conFila((original) => (
      <span style={numeroStyle}>{formatearNumero(original.ponderacion)}</span>
    )),
  },
  {
    Header: 'Calificación',
    id: 'calificacion',
    width: 100,
    Cell: conFila((original) => <span>{texto(original.valor_calificacion)}</span>),
  },
  {
    Header: 'Evaluador',
    id: 'evaluador',
    width: 140,
    Cell: conFila((original) => <span>{nombreEvaluador(original) || SIN_VALOR}</span>),
  },
  {
    Header: 'Últ. modificación',
    id: 'ultimaModificacion',
    width: 140,
    Cell: conFila((original) => (
      <span style={numeroStyle}>{texto(original.ultima_modificacion)}</span>
    )),
  },
  {
    Header: 'Comentario',
    id: 'comentario',
    width: 100,
    sortable: false,
    Cell: conFila((original) =>
      tieneComentario(original) ? (
        <button
          type="button"
          className="btn btn-light btn-sm"
          onClick={() => onVerComentario(original)}
          aria-label={`Ver comentario de ${nombreAlumno(original) || 'alumno'}`}
          style={{ whiteSpace: 'nowrap' }}
        >
          <MessageSquare size={14} aria-hidden="true" /> Ver
        </button>
      ) : (
        <span>{SIN_VALOR}</span>
      ),
    ),
  },
];

const TablaNotasD2l = ({
  rows,
  onVerComentario,
  hasMore = false,
  onLoadMore = null,
  isLoadingMore = false,
}) => {
  const columnas = useMemo(() => construirColumnas(onVerComentario), [onVerComentario]);

  return (
    <div style={{ marginTop: '16px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '4px 8px',
          marginBottom: '8px',
          padding: '0 4px',
        }}
      >
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>Resultados</span>
        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {rows.length} nota{rows.length !== 1 ? 's' : ''}
          {hasMore && ' (hay más resultados)'}
        </span>
      </div>

      <Table
        columns={columnas}
        data={rows}
        defaultPageSize={10}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
      />
    </div>
  );
};

export const notaD2lShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  usuario: PropTypes.string,
  nombre: PropTypes.string,
  apellido: PropTypes.string,
  codigo_oferta: PropTypes.string,
  nombre_oferta: PropTypes.string,
  categoria_elemento: PropTypes.string,
  nombre_elemento: PropTypes.string,
  ponderacion: PropTypes.number,
  numerador_puntos: PropTypes.number,
  denominador_puntos: PropTypes.number,
  valor_calificacion: PropTypes.string,
  ultima_modificacion: PropTypes.string,
  comentarios: PropTypes.string,
  usuario_evaluador: PropTypes.string,
  nombre_evaluador: PropTypes.string,
  apellido_evaluador: PropTypes.string,
});

TablaNotasD2l.propTypes = {
  rows: PropTypes.arrayOf(notaD2lShape).isRequired,
  onVerComentario: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  isLoadingMore: PropTypes.bool,
};

export default TablaNotasD2l;
